import {
    UserLoginDTO,
    UserDTO,
    UserRegistrationDTO,
    UserResetPasswordFirstStep,
    UserResetPasswordDTO,
    UserResetPasswordSecondStepDTO,
    UserForm,
    UserGoogleLoginDTO,
    GetUsersParams,
} from "../domain/models/dto/UserDTO";
import userMapperFactory from "../mappers/UserMapper";
import { CollectionResponse, SimpleResponse } from "../types/ApiResults";
import { ApiServiceImpl, createAsyncThunkFactory } from "./ApiServiceImpl";

export class UserServiceImpl extends ApiServiceImpl {
    private constructor() {
        super();
    }

    static getUserThunk = createAsyncThunkFactory<UserDTO, null>(
        "UserServiceImpl.getUser",
        async () => {
            return this.get("/api/v1/user");
        },
    );

    static getUserByIdThunk = createAsyncThunkFactory<UserDTO, string>(
        "UserServiceImpl.getUserById",
        async (id) => {
            return this.get(`/api/v1/user/${id}`);
        },
    );

    static loginThunk = createAsyncThunkFactory<UserDTO, UserLoginDTO>(
        "UserServiceImpl.login",
        async (args) => {
            if (!args.email || !args.password) {
                throw new Error(`Email or password does not provided.`); // TODO :: can be a translation key
            }
            return this.post<UserDTO>("/api/v1/login", args);
        },
    );

    static logoutThunk = createAsyncThunkFactory<boolean, null>(
        "UserServiceImpl.logout",
        async () => !!(await this.get("/api/v1/logout")),
    );

    static registrationThunk = createAsyncThunkFactory<
        UserDTO,
        UserRegistrationDTO
    >("UserServiceImpl.registration", async (args) => {
        if (
            !args.email ||
            !args.password ||
            !args.confirmPassword ||
            !args.firstName ||
            !args.lastName
        ) {
            throw new Error(`One of the values does not provided.`); // TODO :: can be a translation key
        }
        return this.post<UserDTO>("/api/v1/user", {
            ...args,
            referralToken: args.referralToken || "",
        });
    });

    static updateUserThunk = createAsyncThunkFactory<UserDTO, UserForm>(
        "UserServiceImpl.updateUser",
        async (args) => {
            return this.patch<UserDTO>(
                "/api/v1/user",
                userMapperFactory().toDTO(args),
            );
        },
    );

    static deleteUserThunk = createAsyncThunkFactory<SimpleResponse, null>(
        "UserServiceImpl.deleteUser",
        async () => this.delete("/api/v1/user"),
    );

    static sendRecoveryCodeThunk = createAsyncThunkFactory<
        boolean,
        UserResetPasswordFirstStep
    >(
        "UserServiceImpl.sendRecoveryCode",
        async (args) =>
            !!(await this.patch("/api/v1/user/send-recovery-code", args)),
    );

    static checkRecoveryCodeThunk = createAsyncThunkFactory<
        boolean,
        UserResetPasswordSecondStepDTO
    >(
        "UserServiceImpl.checkRecoveryCode",
        async (args) =>
            !!(await this.patch("/api/v1/user/check-recovery-code", args)),
    );

    static resetPassword = createAsyncThunkFactory<
        boolean,
        UserResetPasswordDTO
    >(
        "UserServiceImpl.resetPassword",
        async (args) =>
            !!(await this.patch("/api/v1/user/reset-password", args)),
    );

    static blockUserThunk = createAsyncThunkFactory<
        SimpleResponse,
        { userId: string }
    >("UserServiceImpl.blockUser", async ({ userId }) =>
        this.patch<SimpleResponse>(`/api/v1/user/${userId}/block`, {}),
    );

    static unblockUserThunk = createAsyncThunkFactory<
        SimpleResponse,
        { userId: string }
    >("UserServiceImpl.unblockUser", async ({ userId }) =>
        this.patch<SimpleResponse>(`/api/v1/user/${userId}/unblock`, {}),
    );

    static googleLoginThunk = createAsyncThunkFactory<
        UserDTO,
        UserGoogleLoginDTO
    >("UserServiceImpl.googleLogin", async (args) => {
        if (!args.code) {
            throw new Error(`Google authentication failed.`);
        }
        return this.post<UserDTO>("/api/v1/google-login", args);
    });

    static getBlockedUsersThunk = createAsyncThunkFactory<
        CollectionResponse<UserDTO>,
        GetUsersParams
    >("UserServiceImpl.getBlockedUsers", async (args) => {
        return this.getCollection("/api/v1/user/blocked", {
            page: args.page,
            limit: args.limit,
            offset: args.offset || 0,
        });
    });
}
